import { isEqual } from 'lodash';
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Button, Input, Tooltip, Spin, AutoComplete } from 'antd';
import { QuestionCircleOutlined, DownOutlined, LoadingOutlined } from '@ant-design/icons';
import { useGetTagKeys, useGetEventNames } from '@src/services/query/applang/queryHooks';
import {
  Wrapper,
  Header,
  Label,
  ConfigurationSection,
  StyledCollapse,
  FormRow,
  RadioGroup,
  RadioOption,
  EventsCheckboxContainer,
  CheckboxRow,
  ActionsWrapper,
} from './styled';
import { useDimensionInterval } from '../DimensionFormContainer';

export interface DimensionFormData {
  name: string;
  tag_key: string;
  from_events: 'all' | 'specific';
  specific_events?: string[];
}

interface IProps {
  dimension?: DimensionFormData;
  isLoading?: boolean;
  onChange?: (dimension: DimensionFormData) => void;
  onSave?: (dimension: DimensionFormData) => void;
  onCancel?: () => void;
}

const DimensionForm: React.FC<IProps> = ({
  dimension,
  isLoading = false,
  onChange,
  onSave,
  onCancel,
}) => {
  const initialFormData: DimensionFormData = {
    name: '',
    tag_key: '',
    from_events: 'all',
    specific_events: [],
  };
  const [formData, setFormData] = useState<DimensionFormData>(initialFormData);

  const [tagKeySearch, setTagKeySearch] = useState('');
  const [debouncedTagKeySearch, setDebouncedTagKeySearch] = useState('');

  // Get time range from global store
  const interval = useDimensionInterval();

  // Debounce tag key search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedTagKeySearch(tagKeySearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [tagKeySearch]);

  // Fetch tag keys
  const { data: tagKeys, isLoading: isLoadingTagKeys } = useGetTagKeys({
    time_range: interval,
    key: debouncedTagKeySearch,
  });

  // Fetch event names (for specific events option)
  const { data: eventNames, isLoading: isLoadingEvents } = useGetEventNames({
    time_range: interval,
    key: '',
  });

  // Transform tag keys to options
  const tagKeyOptions = useMemo(() => {
    if (!tagKeys || tagKeys.length === 0) return [];
    return tagKeys.map((key) => ({
      label: key,
      value: key,
    }));
  }, [tagKeys]);

  // Initialize form data from props
  useEffect(() => {
    if (dimension) {
      setFormData(dimension);
    } else {
      setFormData(initialFormData);
    }
  }, [dimension]);

  // Notify parent of changes
  useEffect(() => {
    onChange?.(formData);
  }, [formData, onChange]);

  const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, name: e.target.value }));
  }, []);

  const handleTagKeyChange = useCallback((value: unknown) => {
    const stringValue = typeof value === 'string' ? value : '';
    setFormData((prev) => ({ ...prev, tag_key: stringValue }));
  }, []);

  const handleFromEventsChange = useCallback((value: 'all' | 'specific') => {
    setFormData((prev) => ({
      ...prev,
      from_events: value,
      specific_events: value === 'all' ? [] : prev.specific_events,
    }));
  }, []);

  const handleEventCheckboxChange = useCallback((eventName: string, checked: boolean) => {
    setFormData((prev) => {
      const currentEvents = prev.specific_events || [];
      const newEvents = checked
        ? [...currentEvents, eventName]
        : currentEvents.filter((e) => e !== eventName);
      return { ...prev, specific_events: newEvents };
    });
  }, []);

  const handleSave = useCallback(() => {
    onSave?.(formData);
  }, [formData, onSave]);

  const loadingSuffix = useMemo(() => {
    if (isLoadingTagKeys) return <LoadingOutlined style={{ color: 'rgba(0, 0, 0, 0.25)' }} />;
    return <DownOutlined />;
  }, [isLoadingTagKeys]);

  const isFormValid = formData.name && formData.tag_key;

  return (
    <Wrapper>
      <Header>
        <Label>Dimension Name :</Label>
        <Input
          placeholder="Type here..."
          value={formData.name}
          onChange={handleNameChange}
          style={{ flex: 1 }}
        />
      </Header>

      <ConfigurationSection>
        <StyledCollapse ghost defaultActiveKey={['1']} expandIconPosition="start">
          <StyledCollapse.Panel header="Configuration" key="1">
            <FormRow>
              <Label>Tag Key :</Label>
              <AutoComplete
                showSearch
                value={formData.tag_key || undefined}
                options={tagKeyOptions}
                placeholder="Select or type here..."
                onChange={handleTagKeyChange}
                onSearch={setTagKeySearch}
                suffixIcon={loadingSuffix}
                filterOption={false}
                allowClear
                style={{ flex: 1 }}
              />
            </FormRow>

            <FormRow>
              <Label>From Events :</Label>
              <RadioGroup>
                <RadioOption checked={formData.from_events === 'all'}>
                  <input
                    type="radio"
                    name="from_events"
                    checked={formData.from_events === 'all'}
                    onChange={() => handleFromEventsChange('all')}
                  />
                  <span>All Applicable Events</span>
                  <Tooltip title="Applies to all events that have this tag key">
                    <QuestionCircleOutlined style={{ color: 'rgba(0, 0, 0, 0.45)' }} />
                  </Tooltip>
                </RadioOption>

                <RadioOption checked={formData.from_events === 'specific'}>
                  <input
                    type="radio"
                    name="from_events"
                    checked={formData.from_events === 'specific'}
                    onChange={() => handleFromEventsChange('specific')}
                  />
                  <span>Specific Event</span>
                  <Tooltip title="Select specific events to apply this dimension">
                    <QuestionCircleOutlined style={{ color: 'rgba(0, 0, 0, 0.45)' }} />
                  </Tooltip>
                </RadioOption>
              </RadioGroup>
            </FormRow>

            {formData.from_events === 'specific' && (
              <EventsCheckboxContainer>
                {isLoadingEvents ? (
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <Spin />
                  </div>
                ) : eventNames && eventNames.length > 0 ? (
                  eventNames.map((eventName) => (
                    <CheckboxRow key={eventName}>
                      <input
                        type="checkbox"
                        checked={formData.specific_events?.includes(eventName) || false}
                        onChange={(e) => handleEventCheckboxChange(eventName, e.target.checked)}
                      />
                      <span>{eventName}</span>
                    </CheckboxRow>
                  ))
                ) : (
                  <div
                    style={{ textAlign: 'center', padding: '20px', color: 'rgba(0, 0, 0, 0.45)' }}
                  >
                    No events available
                  </div>
                )}
              </EventsCheckboxContainer>
            )}
          </StyledCollapse.Panel>
        </StyledCollapse>
      </ConfigurationSection>

      <ActionsWrapper>
        <Button onClick={onCancel} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          type="primary"
          onClick={handleSave}
          disabled={!isFormValid || isLoading}
          loading={isLoading}
        >
          Save
        </Button>
      </ActionsWrapper>
    </Wrapper>
  );
};

export default React.memo(DimensionForm, (prevProps, nextProps) => {
  return isEqual(prevProps, nextProps);
});
